/**
 * La evolución del puntaje, partido a partido.
 *
 * SVG a mano, sin librería: es una sola línea, y una dependencia de gráficos
 * pesaría más que toda la app.
 *
 * El eje X es el número de partido y no la fecha: si alguien no jugó en tres
 * semanas, con fechas la línea tendría un tramo recto larguísimo que no dice
 * nada. La línea de 1000 queda siempre a la vista: es donde arrancaron todos.
 */

export interface PuntoGrafico {
  n: number;
  elo: number;
  fecha: string | null;
}

const ANCHO = 320;
const ALTO = 170;
const MARGEN = { arriba: 12, abajo: 22, izq: 36, der: 10 };
const INICIAL = 1000;

export function GraficoElo({ puntos, pico }: { puntos: PuntoGrafico[]; pico: number }) {
  if (puntos.length < 2) return null;

  const elos = puntos.map((p) => p.elo);
  // El rango siempre incluye 1000, y se estira un poco para que la línea no
  // quede pegada a los bordes.
  let min = Math.min(INICIAL, ...elos);
  let max = Math.max(INICIAL, ...elos);
  const aire = Math.max(10, (max - min) * 0.12);
  min -= aire;
  max += aire;

  const ultimoN = puntos[puntos.length - 1]!.n;
  const anchoUtil = ANCHO - MARGEN.izq - MARGEN.der;
  const altoUtil = ALTO - MARGEN.arriba - MARGEN.abajo;

  const x = (n: number) => MARGEN.izq + (n / ultimoN) * anchoUtil;
  const y = (elo: number) => MARGEN.arriba + ((max - elo) / (max - min)) * altoUtil;

  const linea = puntos.map((p) => `${x(p.n).toFixed(1)},${y(p.elo).toFixed(1)}`).join(' ');
  const base = y(min);
  const area = `${x(0)},${base} ${linea} ${x(ultimoN)},${base}`;

  const ultimo = puntos[puntos.length - 1]!;
  const primero = puntos[0]!;
  const puntoPico = puntos.find((p) => p.elo === pico);
  const subio = ultimo.elo >= primero.elo;

  const color = 'var(--color-acento)';
  const tenue = 'var(--color-borde)';
  const texto = 'var(--color-apagado)';

  return (
    <svg
      viewBox={`0 0 ${ANCHO} ${ALTO}`}
      className="h-auto w-full"
      role="img"
      aria-label={`Evolución del nivel: de ${primero.elo} a ${ultimo.elo} en ${ultimoN} partidos del período`}
    >
      {/* Etiquetas del eje Y: arriba, 1000 y abajo */}
      {[Math.round(max - aire), INICIAL, Math.round(min + aire)]
        .filter((v, i, todos) => todos.indexOf(v) === i)
        .map((valor) => (
          <text
            key={valor}
            x={MARGEN.izq - 6}
            y={y(valor) + 3}
            textAnchor="end"
            fontSize="9"
            fill={texto}
          >
            {valor}
          </text>
        ))}

      {/* Donde arrancaron todos */}
      <line
        x1={MARGEN.izq}
        x2={ANCHO - MARGEN.der}
        y1={y(INICIAL)}
        y2={y(INICIAL)}
        stroke={tenue}
        strokeDasharray="3 3"
      />

      <polygon points={area} fill={color} opacity={subio ? 0.12 : 0.06} />
      <polyline
        points={linea}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* El pico, si no es el arranque */}
      {puntoPico && puntoPico.n > 0 && (
        <circle cx={x(puntoPico.n)} cy={y(puntoPico.elo)} r="3.5" fill={color} />
      )}

      {/* Dónde está hoy */}
      <circle
        cx={x(ultimo.n)}
        cy={y(ultimo.elo)}
        r="4"
        fill="var(--color-fondo)"
        stroke={color}
        strokeWidth="2"
      />

      {/* Eje X */}
      <text x={MARGEN.izq} y={ALTO - 6} fontSize="9" fill={texto}>
        inicio
      </text>
      <text x={ANCHO - MARGEN.der} y={ALTO - 6} fontSize="9" fill={texto} textAnchor="end">
        partido {ultimoN}
      </text>
    </svg>
  );
}
